import { useCallback, useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { apiFetch } from "../../services/apiClient";

export type ProductoRow = {
  id: number;
  tipo: string;
  nombre: string;
  marca?: string | null;
  cantidadDeUnidades?: number;
  cantidadDePacks?: number;
  monto: number;
  fecha: string;
  imagenes?: string[] | null;
  descripcion?: string;
};

export function useProductosSection(
  clientId: number | null | undefined,
  selectedPropId: number | null
) {
  const [rows, setRows] = useState<ProductoRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [totals, setTotals] = useState<{ monto: number; currency: string } | null>(null);
  const [month, setMonth] = useState<Dayjs | null>(null);

  const fetchRows = useCallback(
    async (p = page, s = size) => {
      if (!selectedPropId) return;
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("page", String(p));
        params.set("size", String(s));
        params.set("propId", String(selectedPropId));
        if (month) {
          params.set("from", month.startOf("month").toISOString());
          params.set("to", month.endOf("month").toISOString());
        }

        // Admin no tiene clientId; usa 0 como placeholder (backend lo ignora para admin)
        const idParam = clientId ?? 0;
        const json = await apiFetch<any>(
          `/finanzas/productos/${idParam}?${params.toString()}`,
        );
        if (!json) return;

        setRows(json.data || []);
        setTotal(json.total || 0);
        setPage(json.page || 1);
        setSize(json.size || 20);
        setTotals(json.totals || null);
      } finally {
        setLoading(false);
      }
    },
    [clientId, selectedPropId, page, size, month]
  );

  useEffect(() => {
    if (selectedPropId) fetchRows(1, size);
  }, [clientId, selectedPropId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedPropId) fetchRows(1, size);
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPaginate = (p: number, s: number) => {
    setSize(s);
    fetchRows(p, s);
  };

  return { rows, total, page, size, loading, totals, setMonth, onPaginate };
}
